import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Alert, AlertTitle, AlertDescription } from '@/components/assist/ui/alert';
import { api } from '@/api/client';

interface PhaseOutRecord {
  id: string;
  user_email: string;
  role: string;
  suspend_after: string;
  status: string;
}

/**
 * Global banner shown to any user who has a pending phase-out record.
 * Warns the user that their account will be suspended on a specific date.
 *
 * Fetches GET /api/users/me/phase-out (returns a single record or null).
 * Renders nothing when no record exists or the request fails.
 */
export function PhaseOutUserWarning() {
  const [record, setRecord] = useState<PhaseOutRecord | null>(null);

  useEffect(() => {
    api
      .get<PhaseOutRecord | null>('/users/me/phase-out')
      .then((data) => {
        if (data && data.id) setRecord(data);
      })
      .catch((error) => {
        console.warn('Failed to fetch phase-out status:', error);
      });
  }, []);

  if (!record) return null;

  const suspendDate = new Date(record.suspend_after).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <Alert className="mb-4 border-red-300 bg-red-50/60 dark:border-red-700 dark:bg-red-900/20">
      <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
      <AlertTitle className="text-red-800 dark:text-red-200">
        Account suspension scheduled
      </AlertTitle>
      <AlertDescription className="text-red-700 dark:text-red-300">
        Your <strong>{record.role}</strong> access will be suspended on{' '}
        <strong>{suspendDate}</strong> due to a plan change.
        Please contact your administrator for more information.
      </AlertDescription>
    </Alert>
  );
}
